import { useEffect, useState } from 'react'
import { api, type pending_payment } from '../api'
import { format_amount, format_date, format_timestamp, is_outgoing } from '../format'

/**
 * Der Bildschirm, an dem die meiste Zeit verbracht wird: PDF links,
 * Zahlungsdaten rechts, und nach der Entscheidung direkt weiter zur nächsten
 * zu prüfenden Zahlung — ohne Umweg über eine Liste.
 */
export function ReviewPage() {
  const [queue, set_queue] = useState<pending_payment[]>([])
  const [index, set_index] = useState(0)
  const [receipt_index, set_receipt_index] = useState(0)
  const [note, set_note] = useState('')
  const [error, set_error] = useState<string | null>(null)
  const [busy, set_busy] = useState(false)
  const [loaded, set_loaded] = useState(false)

  async function load() {
    const pending = await api.pending_payments()
    set_queue(pending)
    set_index(0)
    set_receipt_index(0)
    set_loaded(true)
  }

  useEffect(() => {
    void load()
  }, [])

  const current = queue[index]

  async function decide(action: () => Promise<unknown>) {
    set_busy(true)
    set_error(null)
    try {
      await action()
      set_note('')
      set_receipt_index(0)
      // Die erledigte Zahlung fällt aus der Warteschlange; der Index bleibt
      // stehen und zeigt damit von selbst auf die nächste.
      const remaining = queue.filter((_, position) => position !== index)
      set_queue(remaining)
      if (index >= remaining.length) set_index(Math.max(0, remaining.length - 1))
    } catch (caught) {
      set_error(caught instanceof Error ? caught.message : String(caught))
    } finally {
      set_busy(false)
    }
  }

  if (!loaded) return <div className="page muted">Wird geladen …</div>

  if (!current) {
    return (
      <div className="page">
        <h1>Zu prüfen</h1>
        <div className="card">
          <p className="muted">Nichts zu prüfen — alle Einreichungen sind entschieden.</p>
          <button onClick={load}>Neu laden</button>
        </div>
      </div>
    )
  }

  const receipt = current.receipts[receipt_index]

  return (
    <div className="page page--wide">
      <div className="row">
        <h1 style={{ margin: 0 }}>Zu prüfen</h1>
        <span className="muted">
          {index + 1} von {queue.length}
        </span>
        <span style={{ flex: 1 }} />
        <button disabled={index === 0} onClick={() => set_index(index - 1)}>
          Zurück
        </button>
        <button disabled={index >= queue.length - 1} onClick={() => set_index(index + 1)}>
          Überspringen
        </button>
      </div>

      {error && <div className="notice notice--error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="review" style={{ marginTop: 12 }}>
        <div>
          {receipt ? (
            <>
              <div className="row" style={{ marginBottom: 8 }}>
                {current.receipts.map((item, position) => (
                  <button
                    key={item.id}
                    className={position === receipt_index ? 'primary' : ''}
                    onClick={() => set_receipt_index(position)}
                  >
                    {item.file_name}
                  </button>
                ))}
                <span style={{ flex: 1 }} />
                <a
                  className="button"
                  href={api.receipt_download_url(receipt.id)}
                  title={`${receipt.file_name} speichern`}
                >
                  Herunterladen
                </a>
              </div>
              <iframe title={receipt.file_name} src={api.receipt_file_url(receipt.id)} />
            </>
          ) : (
            <div className="card muted">Kein Beleg vorhanden.</div>
          )}
        </div>

        <div>
          <div className="card">
            <h2>{current.user_name}</h2>
            <table>
              <tbody>
                <tr>
                  <th>Datum</th>
                  <td>{format_date(current.booking_date)}</td>
                </tr>
                <tr>
                  <th>Gegenpartei</th>
                  <td>{current.counterparty_name}</td>
                </tr>
                <tr>
                  <th>Zweck</th>
                  <td>{current.purpose}</td>
                </tr>
                <tr>
                  <th>IBAN</th>
                  <td className="small">{current.iban ?? '—'}</td>
                </tr>
                <tr>
                  <th>Betrag</th>
                  <td className={`amount ${is_outgoing(current.amount) ? 'outgoing' : 'incoming'}`}>
                    {format_amount(current.amount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {current.rejection_notes.length > 0 && (
            <div className="card">
              <h2>Frühere Ablehnungen</h2>
              {current.rejection_notes.map((entry) => (
                <p key={entry.id} className="small">
                  <span className="muted">{format_timestamp(entry.created_at)}:</span> {entry.note}
                </p>
              ))}
            </div>
          )}

          <div className="card">
            <button
              className="primary"
              disabled={busy}
              onClick={() => decide(() => api.accept_payment(current.id))}
            >
              Annehmen
            </button>
            <p className="small muted">
              Nach der Annahme kann der Benutzer die Belege dieser Zahlung nicht mehr ändern.
            </p>

            <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '12px 0' }} />

            <div className="field">
              <label htmlFor="note">Ablehnungsgrund (wird dem Benutzer angezeigt)</label>
              <textarea
                id="note"
                rows={3}
                value={note}
                onChange={(event) => set_note(event.target.value)}
              />
            </div>
            <button
              className="danger"
              disabled={busy || note.trim().length === 0}
              onClick={() => decide(() => api.reject_payment(current.id, note.trim()))}
            >
              Ablehnen und benachrichtigen
            </button>
            <p className="small muted">
              Die Ablehnung verschickt sofort eine E-Mail mit dem Grund und einem frischen
              Anmeldelink.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
